import { LicenseErrorInfo, getLicenseErrorInfo } from 'constants/errors/license_error_codes';
import { findAssignedSkillDive } from 'hooks/useSkillDiveToggleState';
import { Invite } from 'pages/Users/components/InviteUsers/AddByEmails/invitesReducer';
import { sendUserInvites } from 'services/userInvites';
import { removeAddonLicense, setAddonLicense, setLicense, setTeams } from 'services/users';
import { buildInvitesWithTeams, isRefusal, keepsSkillDiveSeat } from 'utils/invites';
import {
  ChangeResult,
  EmailInvitesResult,
  ExistingUserChange,
  ExistingUserResult,
  TeamInviteOutcome,
} from './types';

interface Attempt {
  result: ChangeResult;
  licenseError?: LicenseErrorInfo;
}

const attempt = async (request: () => Promise<unknown>): Promise<Attempt> => {
  try {
    const response = (await request()) as { data?: unknown } | undefined;
    return isRefusal(response?.data) ? { result: 'failed' } : { result: 'applied' };
  } catch (error) {
    return { result: 'failed', licenseError: getLicenseErrorInfo(error) ?? undefined };
  }
};

// Applies one user's changes in order; only adding Skill Dive depends on training succeeding.
export const applyExistingUser = async (
  invite: Invite,
  teamId: string,
  skillDiveAddonId?: string
): Promise<ExistingUserResult> => {
  const { existing } = invite;
  const changes: ExistingUserChange[] = [];

  const changesTraining = invite.has_license !== existing.has_license;
  const seat = findAssignedSkillDive(existing);
  const wantsSkillDive = Boolean(invite.has_license && invite.has_skill_dive);
  const addsSkillDive = wantsSkillDive && !seat;
  const removesSkillDive =
    !wantsSkillDive && Boolean(seat) && (invite.has_license || existing.has_license);
  const isManager = Boolean(invite.userTeams?.[0]?.is_manager);
  // A Team Manager can't change another manager's access, so managers are made last.
  const promotesLast = isManager && (changesTraining || addsSkillDive || removesSkillDive);

  // Setting teams replaces them all, so send their current teams plus this one.
  const teamsWith = (manager: boolean) => [
    ...existing.teams.filter(({ team_id }) => team_id !== teamId),
    { team_id: teamId, is_manager: manager },
  ];
  const team = await attempt(() =>
    setTeams({ user_invite_id: invite.id, teams: teamsWith(isManager && !promotesLast) })
  );
  changes.push({ kind: 'team', assign: true, ...team });

  let training: ChangeResult | null = null;
  if (changesTraining) {
    const change = await attempt(() =>
      setLicense({ ids: [invite.id], license: invite.has_license })
    );
    training = change.result;
    changes.push({ kind: 'training', assign: invite.has_license, ...change });
  }

  if (addsSkillDive) {
    const change: Attempt =
      training === 'failed'
        ? { result: 'skipped' }
        : await attempt(() =>
            setAddonLicense({ assigned_to: existing.user, addon: skillDiveAddonId })
          );
    changes.push({ kind: 'skill_dive', assign: true, ...change });
  } else if (removesSkillDive) {
    // Skip seats on users without training; removing training already removes the add-ons.
    const change: Attempt =
      training === 'applied' && !invite.has_license
        ? { result: 'applied' }
        : await attempt(() => removeAddonLicense({ license_addon_id: seat.id }));
    changes.push({ kind: 'skill_dive', assign: false, ...change });
  }

  if (promotesLast && team.result === 'applied') {
    const promotion: Attempt = changes.every(({ result }) => result === 'applied')
      ? await attempt(() => setTeams({ user_invite_id: invite.id, teams: teamsWith(true) }))
      : { result: 'skipped' };
    if (promotion.result !== 'applied') {
      changes.push({ kind: 'manager', assign: true, ...promotion });
    }
  }

  return { invite, changes };
};

// Sends the new emails as one all-or-nothing batch.
const sendEmailInvites = async (
  invites: Invite[],
  message: string | null
): Promise<EmailInvitesResult> => {
  const { result, licenseError } = await attempt(() =>
    sendUserInvites({ message, invites: buildInvitesWithTeams(invites, { withSkillDive: true }) })
  );
  return { count: invites.length, sent: result === 'applied', licenseError };
};

interface ApplyTeamInvites {
  teamId: string;
  existingUsers: Invite[];
  emailInvites: Invite[];
  message: string | null;
  skillDiveAddonId?: string;
}

// Whether the user gives up a training or Skill Dive seat here.
const releasesSeat = (invite: Invite): boolean =>
  (invite.existing.has_license && !invite.has_license) ||
  (Boolean(findAssignedSkillDive(invite.existing)) && !keepsSkillDiveSeat(invite));

// Whether the user takes a training or Skill Dive seat they don't hold yet.
const takesSeat = (invite: Invite): boolean =>
  (invite.has_license && !invite.existing.has_license) ||
  (Boolean(invite.has_skill_dive) && !findAssignedSkillDive(invite.existing));

// The round a user is sent in: those only giving up seats, then those giving up one kind and
// taking the other, then the rest. Every seat is given up before any seat of its kind is taken.
const round = (invite: Invite): number => {
  if (!releasesSeat(invite)) return 2;
  return takesSeat(invite) ? 1 : 0;
};

// Applies existing users' changes, then sends the email invites, keeping a result for each.
export const applyTeamInvites = async ({
  teamId,
  existingUsers,
  emailInvites,
  message,
  skillDiveAddonId,
}: ApplyTeamInvites): Promise<TeamInviteOutcome> => {
  const applyRound = (step: number) =>
    Promise.all(
      existingUsers
        .filter((invite) => round(invite) === step)
        .map((invite) => applyExistingUser(invite, teamId, skillDiveAddonId))
    );
  // The rounds run one after another, in order.
  const results = [...(await applyRound(0)), ...(await applyRound(1)), ...(await applyRound(2))];
  const existingResults = existingUsers.map((invite) =>
    results.find((result) => result.invite.id === invite.id)
  );
  const emailResult = emailInvites.length ? await sendEmailInvites(emailInvites, message) : null;
  return { existingUsers: existingResults, emailInvites: emailResult };
};

export const outcomeFailed = ({ existingUsers, emailInvites }: TeamInviteOutcome): boolean =>
  existingUsers.some(({ changes }) => changes.some(({ result }) => result !== 'applied')) ||
  (emailInvites !== null && !emailInvites.sent);

export const outcomeSavedAnything = ({ existingUsers, emailInvites }: TeamInviteOutcome): boolean =>
  existingUsers.some(({ changes }) => changes.some(({ result }) => result === 'applied')) ||
  Boolean(emailInvites?.sent);

export const outcomeHitLicenseError = ({
  existingUsers,
  emailInvites,
}: TeamInviteOutcome): boolean =>
  existingUsers.some(({ changes }) => changes.some(({ licenseError }) => Boolean(licenseError))) ||
  Boolean(emailInvites?.licenseError);
